/* The frame: the rail on the left, the filters on top, the panel in the middle. One filter
 * row for the whole page, since per-card filters would let two panels disagree about what
 * "now" means.
 */

import { useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AppShell,
  InlineSelect,
  SidebarFooterActions,
  SidebarNavItem,
  SidebarSection,
  SidebarShell,
  ThemeToggleButton,
  TopbarShell,
  type SidebarNavItemProps,
  cn,
} from '@nextleap-al/admin-ui';

import { ALL_TABS, NAV, labelOf, subOf, visible } from '../nav';
import { useApp } from '../state/app';

/** `NavLink` accepts a richer `to` than the library's link contract declares. Only a string
 *  is ever passed, so the cast narrows rather than widens. */
const RouterLink = NavLink as unknown as SidebarNavItemProps['LinkComponent'];

/* The chosen row's tile: the accent as a solid block, with the glyph printed on it. Ink
 * rather than a literal white, because the token is white on the light surface and the near
 * navy in the dark, where the accent green is light enough that white on it is unreadable. */
const TILE = { background: 'var(--brand-green-ink)', color: 'var(--text-inverse)' };

const RANGES = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: '365', label: 'Last 12 months' },
  { value: '3650', label: 'All time' },
];

function Mark() {
  return (
    <span
      className="flex h-9 w-9 flex-none items-center justify-center rounded-[var(--radius-md)] bg-[var(--primary-light)] text-[var(--primary)]"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
        <path d="M3 21h18" />
        <path d="M6.5 21V11" />
        <path d="M12 21V4" />
        <path d="M17.5 21v-6" />
      </svg>
    </span>
  );
}

export function Shell() {
  const {
    me, days, setDays, system, setSystem, simple, systemLabel, theme, toggleTheme, refetching,
    signOut,
  } = useApp();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  const context = { simple, superadmin: me.is_superadmin };

  // Prefix, not equality: a tab may have pages under it (`/integrations/3`), and those have
  // to keep the rail lit and the heading filled.
  const onTab = (id: string) =>
    location.pathname === `/${id}` || location.pathname.startsWith(`/${id}/`);
  const current = ALL_TABS.find((tab) => onTab(tab.id));

  const whoami = me.is_superadmin
    ? `${me.user} · superadmin`
    : `${me.user} · ${me.systems.map(systemLabel).join(', ') || 'no system'}`;

  // An admin confined to a single system has nothing to pick between, so the filter states
  // the system instead of offering a choice.
  const oneSystem = !me.all_systems && me.systems.length < 2;
  const systemOptions = [
    ...(oneSystem ? [] : [{ value: 'all', label: 'All systems' }]),
    // The value stays the key every request is filtered by; only the wording changes.
    ...me.systems.map((s) => ({ value: s, label: systemLabel(s) })),
  ];

  return (
    <AppShell
      sidebarCollapsed={collapsed}
      contentClassName="p-5 lg:p-6"
      sidebar={
        <SidebarShell
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onMobileClose={() => setMobileOpen(false)}
          header={
            <div className="flex items-center gap-3 overflow-hidden">
              <Mark />
              {!collapsed && (
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-[var(--text-primary)]">
                    NextI Analytics
                  </div>
                  <div className="truncate text-xs text-[var(--text-muted)]">{whoami}</div>
                </div>
              )}
            </div>
          }
          footer={
            // No view switch: which page you get follows the role and nothing else.
            <SidebarFooterActions
              collapsed={collapsed}
              onCollapse={() => setCollapsed((c) => !c)}
              onLogout={signOut}
              logoutLabel="Sign out"
            />
          }
        >
          {NAV.map((group) => {
            const tabs = group.tabs.filter((tab) => visible(tab, context));
            // A group heading with every item under it hidden is a heading for nothing.
            if (!tabs.length) return null;
            return (
              <SidebarSection
                key={group.label}
                label={(simple && group.simpleLabel) || group.label}
                collapsed={collapsed}
              >
                {tabs.map((tab) => {
                  const active = onTab(tab.id);
                  return (
                    <SidebarNavItem
                      key={tab.id}
                      // Every glyph in the rail sits in the same 28px square, lit or not. The
                      // box is what holds the icons in one column and the row height still
                      // while the selection moves; only the fill says which row is current.
                      icon={
                        <span
                          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg"
                          style={active ? TILE : undefined}
                        >
                          {tab.icon}
                        </span>
                      }
                      label={labelOf(tab, simple)}
                      href={`/${tab.id}`}
                      active={active}
                      collapsed={collapsed}
                      onClick={() => setMobileOpen(false)}
                      LinkComponent={RouterLink}
                      // The library ends its active row with a dot, which the tile now says
                      // twice. An empty trailing node takes that slot rather than a fork.
                      trailing={active ? <></> : undefined}
                      // Left padding down to the vertical one, so the tile is inset by the same
                      // 6px on three sides; the right stays where the library had it. Skipped
                      // while collapsed, where the icon is centred in the library's own `px-2`
                      // and a left inset would push it off centre. The weight on the chosen
                      // label is a stroke, in styles.css, and not a heavier font: a heavier
                      // font is a wider one, and the row would shuffle sideways as the
                      // selection moved.
                      className={cn(
                        'py-1.5',
                        !collapsed && 'pl-1.5 pr-3',
                        active && 'nav-selected-bg nav-selected-label',
                      )}
                    />
                  );
                })}
              </SidebarSection>
            );
          })}
        </SidebarShell>
      }
      topbar={
        <TopbarShell
          onMenuClick={() => setMobileOpen(true)}
          left={
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold text-[var(--text-primary)]">
                {current ? labelOf(current, simple) : 'NextI Analytics'}
              </h1>
              {current && (
                <p className="hidden truncate text-xs text-[var(--text-muted)] lg:block">
                  {subOf(current, simple)}
                </p>
              )}
            </div>
          }
          right={
            <div className="flex items-center gap-3">
              <InlineSelect
                label="System"
                value={system}
                onChange={(value) => setSystem(String(value))}
                options={systemOptions}
                width="w-[150px]"
                disabled={systemOptions.length < 2}
                className="hidden md:flex"
              />
              <InlineSelect
                label="Period"
                value={String(days)}
                onChange={(value) => setDays(Number(value))}
                options={RANGES}
                width="w-[150px]"
              />
              <ThemeToggleButton isDark={theme === 'dark'} onToggle={toggleTheme} />
            </div>
          }
        />
      }
    >
      {/* Nothing here says "working". There was a bar across the top, and on a reload it was
          the one thing that appeared, sat over an empty page and then went away — which is
          the sequence this frame exists to not have. A request over an answer already on
          screen is said by that answer dimming, below; a first load is said by the browser's
          own tab. */}

      {/* No numbers here either. Each panel carries its own row, because a figure printed in
          the frame is read as belonging to whatever page happens to be open under it. */}

      {/* The system picker moves under the toolbar on a narrow screen, where the topbar has
          no room for two of them. */}
      {systemOptions.length > 1 && (
        <div className="mb-4 md:hidden">
          <InlineSelect
            label="System"
            value={system}
            onChange={(value) => setSystem(String(value))}
            options={systemOptions}
            width="w-[150px]"
          />
        </div>
      )}

      {/* Dimmed only over an answer that is already here: on a first load there is nothing
          to hold back, and fading the skeleton the moment it appears is a flicker in the
          middle of the very sequence the frame in index.html exists to smooth. */}
      <div className={refetching ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        <Outlet />
      </div>
    </AppShell>
  );
}
